/**
 * Custom hook for managing intake print functionality.
 * Generates a print-ready HTML document grouped by meal category,
 * with configurable column visibility.
 */

import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { calculateNutrition } from "../utils";
import type { Prijem, PrijemKategoria } from "../types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IntakePrintSettings {
  showAmount: boolean;
  showEnergy: boolean;
  showProtein: boolean;
  showFat: boolean;
  showCarbs: boolean;
  showSatFat: boolean;
  showFiber: boolean;
  showSodium: boolean;
  showNotes: boolean;
  showRecipeIngredients: boolean;
  showCategoryTotals: boolean;
  showDailyTotals: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIntakePrint() {
  const { t, locale } = useTranslation();

  const [printSettings, setPrintSettings] = useState<IntakePrintSettings>({
    showAmount: true,
    showEnergy: true,
    showProtein: true,
    showFat: true,
    showCarbs: true,
    showSatFat: false,
    showFiber: false,
    showSodium: false,
    showNotes: false,
    showRecipeIngredients: true,
    showCategoryTotals: true,
    showDailyTotals: true,
  });

  const toggleAllColumns = (checked: boolean) => {
    setPrintSettings({
      showAmount: checked,
      showEnergy: checked,
      showProtein: checked,
      showFat: checked,
      showCarbs: checked,
      showSatFat: checked,
      showFiber: checked,
      showSodium: checked,
      showNotes: checked,
      showRecipeIngredients: checked,
      showCategoryTotals: checked,
      showDailyTotals: checked,
    });
  };

  const areAllColumnsSelected = Object.values(printSettings).every(Boolean);

  // ── HTML generation ────────────────────────────────────────────────────────

  const generatePrintHtml = (
    prijem: Prijem[],
    prijemKategorie: PrijemKategoria[],
    clientAlias: string,
    datum: string
  ): string => {
    // Get the resolved locale string (e.g., "sk-SK" or "en-US")
    const resolvedLocale =
      typeof locale === "string" ? locale : locale === "sk" ? "sk-SK" : "en-US";

    const formattedDate = new Date(datum).toLocaleDateString(resolvedLocale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Group prijem by category
    const byKategoria: Record<number, Prijem[]> = {};
    for (const item of prijem) {
      const katId = item.prijemKategoriaId ?? 0;
      if (!byKategoria[katId]) byKategoria[katId] = [];
      byKategoria[katId].push(item);
    }

    // Compute totals helper
    const sumNutrition = (items: Prijem[]) => {
      let kcal = 0,
        prot = 0,
        fat = 0,
        carb = 0,
        nmk = 0,
        fib = 0,
        sod = 0;
      for (const item of items) {
        const n = calculateNutrition(item.potravina, item.hmotnost);
        kcal += parseFloat(n.energiaKcal) || 0;
        prot += parseFloat(n.proteinG) || 0;
        fat += parseFloat(n.tukG) || 0;
        carb += parseFloat(n.sacharidG) || 0;
        nmk += parseFloat(n.nmkG) || 0;
        fib += parseFloat(n.vlakninaG) || 0;
        sod += parseFloat(n.sodikG) || 0;
      }
      return {
        kcal: kcal.toFixed(1),
        prot: prot.toFixed(1),
        fat: fat.toFixed(1),
        carb: carb.toFixed(1),
        nmk: nmk.toFixed(1),
        fib: fib.toFixed(1),
        sod: sod.toFixed(2),
      };
    };

    const dailyTotals = sumNutrition(prijem);

    const escapeHtml = (value: string | number | null | undefined) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const visibleColumnCount =
      1 +
      Number(printSettings.showAmount) +
      Number(printSettings.showEnergy) +
      Number(printSettings.showProtein) +
      Number(printSettings.showFat) +
      Number(printSettings.showCarbs) +
      Number(printSettings.showSatFat) +
      Number(printSettings.showFiber) +
      Number(printSettings.showSodium) +
      Number(printSettings.showNotes);

    // Build category sections
    const categorySections = prijemKategorie
      .map(kat => {
        const items = byKategoria[kat.id] || [];
        if (items.length === 0) return "";

        const catTotals = sumNutrition(items);
        const catName = kat.name ?? kat.nazov ?? String(kat.id);

        const rows = items
          .map(item => {
            const n = calculateNutrition(item.potravina, item.hmotnost);
            const weight = item.hmotnost || item.potravina.balenieG || "100";
            const ingredientRows =
              printSettings.showRecipeIngredients &&
              item.isRecipe &&
              item.recipeIngredients &&
              item.recipeIngredients.length > 0
                ? `
                  <tr class="ingredients-row">
                    <td colspan="${visibleColumnCount}">
                      <div class="ingredients-label">${t("intake:print.recipeIngredients")}</div>
                      <ul class="ingredients-list">
                        ${item.recipeIngredients
                          .map(
                            ing =>
                              `<li>${escapeHtml(ing.nazov)} <span>${escapeHtml(ing.hmotnostG)} g</span></li>`
                          )
                          .join("")}
                      </ul>
                    </td>
                  </tr>
                `
                : "";
            return `
              <tr>
                <td>${escapeHtml(item.potravina.nazov)}</td>
                ${printSettings.showAmount ? `<td class="text-right">${escapeHtml(weight)}</td>` : ""}
                ${printSettings.showEnergy ? `<td class="text-right">${escapeHtml(n.energiaKcal)}</td>` : ""}
                ${printSettings.showProtein ? `<td class="text-right">${escapeHtml(n.proteinG)}</td>` : ""}
                ${printSettings.showFat ? `<td class="text-right">${escapeHtml(n.tukG)}</td>` : ""}
                ${printSettings.showCarbs ? `<td class="text-right">${escapeHtml(n.sacharidG)}</td>` : ""}
                ${printSettings.showSatFat ? `<td class="text-right">${escapeHtml(n.nmkG)}</td>` : ""}
                ${printSettings.showFiber ? `<td class="text-right">${escapeHtml(n.vlakninaG)}</td>` : ""}
                ${printSettings.showSodium ? `<td class="text-right">${escapeHtml(n.sodikG)}</td>` : ""}
                ${printSettings.showNotes ? `<td>${escapeHtml(item.poznamka || "-")}</td>` : ""}
              </tr>
              ${ingredientRows}
            `;
          })
          .join("");

        const totalsRow = printSettings.showCategoryTotals
          ? `
            <tr class="totals-row">
              <td><strong>${t("intake:print.totalLabel")}</strong></td>
              ${printSettings.showAmount ? `<td></td>` : ""}
              ${printSettings.showEnergy ? `<td class="text-right"><strong>${catTotals.kcal}</strong></td>` : ""}
              ${printSettings.showProtein ? `<td class="text-right"><strong>${catTotals.prot}</strong></td>` : ""}
              ${printSettings.showFat ? `<td class="text-right"><strong>${catTotals.fat}</strong></td>` : ""}
              ${printSettings.showCarbs ? `<td class="text-right"><strong>${catTotals.carb}</strong></td>` : ""}
              ${printSettings.showSatFat ? `<td class="text-right"><strong>${catTotals.nmk}</strong></td>` : ""}
              ${printSettings.showFiber ? `<td class="text-right"><strong>${catTotals.fib}</strong></td>` : ""}
              ${printSettings.showSodium ? `<td class="text-right"><strong>${catTotals.sod}</strong></td>` : ""}
              ${printSettings.showNotes ? `<td></td>` : ""}
            </tr>
          `
          : "";

        return `
          <div class="category-section">
            <div class="category-title">${catName}</div>
            <table class="print-table">
              <thead>
                <tr>
                  <th>${t("intake:print.food")}</th>
                  ${printSettings.showAmount ? `<th class="text-right">${t("intake:print.columns.showAmount")}</th>` : ""}
                  ${printSettings.showEnergy ? `<th class="text-right">${t("intake:print.columns.showEnergy")}</th>` : ""}
                  ${printSettings.showProtein ? `<th class="text-right">${t("intake:print.columns.showProtein")}</th>` : ""}
                  ${printSettings.showFat ? `<th class="text-right">${t("intake:print.columns.showFat")}</th>` : ""}
                  ${printSettings.showCarbs ? `<th class="text-right">${t("intake:print.columns.showCarbs")}</th>` : ""}
                  ${printSettings.showSatFat ? `<th class="text-right">${t("intake:print.columns.showSatFat")}</th>` : ""}
                  ${printSettings.showFiber ? `<th class="text-right">${t("intake:print.columns.showFiber")}</th>` : ""}
                  ${printSettings.showSodium ? `<th class="text-right">${t("intake:print.columns.showSodium")}</th>` : ""}
                  ${printSettings.showNotes ? `<th>${t("intake:print.columns.showNotes")}</th>` : ""}
                </tr>
              </thead>
              <tbody>
                ${rows}
                ${totalsRow}
              </tbody>
            </table>
          </div>
        `;
      })
      .join("");

    const dailyTotalsSection = printSettings.showDailyTotals
      ? `
        <div class="daily-totals">
          <strong>${t("intake:print.dailyTotal")}:</strong>
          ${printSettings.showEnergy ? `&nbsp;&nbsp;${t("intake:print.energy")}: <strong>${dailyTotals.kcal}</strong>` : ""}
          ${printSettings.showProtein ? `&nbsp;&nbsp;${t("intake:print.protein")}: <strong>${dailyTotals.prot}g</strong>` : ""}
          ${printSettings.showFat ? `&nbsp;&nbsp;${t("intake:print.fat")}: <strong>${dailyTotals.fat}g</strong>` : ""}
          ${printSettings.showCarbs ? `&nbsp;&nbsp;${t("intake:print.carbs")}: <strong>${dailyTotals.carb}g</strong>` : ""}
          ${printSettings.showSatFat ? `&nbsp;&nbsp;${t("intake:print.satFat")}: <strong>${dailyTotals.nmk}g</strong>` : ""}
          ${printSettings.showFiber ? `&nbsp;&nbsp;${t("intake:print.fiber")}: <strong>${dailyTotals.fib}g</strong>` : ""}
          ${printSettings.showSodium ? `&nbsp;&nbsp;${t("intake:print.sodium")}: <strong>${dailyTotals.sod}g</strong>` : ""}
        </div>
      `
      : "";

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t("intake:print.title")} - ${clientAlias} - ${datum}</title>
        <meta charset="utf-8" />
        <style>
          @media print {
            @page { margin: 15mm; }
            body { font-size: 10px; }
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 11px;
            color: #000;
            line-height: 1.3;
            margin: 0;
            padding: 15px;
          }
          .print-header {
            text-align: center;
            margin-bottom: 15px;
            border-bottom: 2px solid #000;
            padding-bottom: 8px;
          }
          .print-title { font-size: 16px; font-weight: bold; margin-bottom: 3px; }
          .print-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            font-size: 10px;
            background-color: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
          }
          .category-section { margin-bottom: 18px; }
          .category-title {
            font-size: 12px;
            font-weight: bold;
            background-color: #e9ecef;
            padding: 4px 8px;
            margin-bottom: 4px;
            border-left: 3px solid #495057;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          .print-table th {
            background-color: #f1f3f5;
            border: 1px solid #dee2e6;
            padding: 4px 6px;
            font-weight: bold;
            white-space: nowrap;
          }
          .print-table td {
            border: 1px solid #dee2e6;
            padding: 4px 6px;
            vertical-align: top;
          }
          .totals-row td { background-color: #f8f9fa; }
          .ingredients-row td { background-color: #fbfbfb; font-size: 9px; }
          .ingredients-label { font-weight: bold; margin-bottom: 2px; }
          .ingredients-list {
            margin: 0;
            padding-left: 14px;
            columns: 2;
          }
          .ingredients-list li { break-inside: avoid; margin-bottom: 1px; }
          .ingredients-list span { color: #555; }
          .daily-totals {
            margin-top: 16px;
            padding: 8px;
            border-top: 2px solid #000;
            font-size: 11px;
          }
          .print-footer {
            margin-top: 20px;
            font-size: 9px;
            color: #666;
            text-align: center;
            border-top: 1px solid #dee2e6;
            padding-top: 8px;
          }
          .text-right { text-align: right; }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">${t("intake:print.title")}</div>
        </div>
        <div class="print-meta">
          <div>
            <strong>${t("intake:print.client")}:</strong> ${clientAlias}<br>
            <strong>${t("intake:print.date")}:</strong> ${formattedDate}
          </div>
          <div style="text-align:right;">
            <strong>${t("intake:print.energy")}:</strong> ${dailyTotals.kcal} kcal<br>
            <strong>${t("intake:print.protein")}:</strong> ${dailyTotals.prot}g &nbsp;
            <strong>${t("intake:print.fat")}:</strong> ${dailyTotals.fat}g &nbsp;
            <strong>${t("intake:print.carbs")}:</strong> ${dailyTotals.carb}g
          </div>
        </div>
        ${categorySections}
        ${dailyTotalsSection}
        <div class="print-footer">
          ${t("intake:print.generatedOn")} ${new Date().toLocaleString(resolvedLocale)}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                setTimeout(function() { window.close(); }, 100);
              };
            }, 250);
          };
        </script>
      </body>
      </html>
    `;
  };

  // ── Public handler ─────────────────────────────────────────────────────────

  const handlePrint = (
    prijem: Prijem[],
    prijemKategorie: PrijemKategoria[],
    clientAlias: string,
    datum: string
  ) => {
    if (prijem.length === 0) {
      toast.error(t("intake:print.noRecords"));
      return;
    }

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {
      toast.error(t("intake:print.popupBlocked"));
      return;
    }

    const html = generatePrintHtml(prijem, prijemKategorie, clientAlias, datum);
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return {
    printSettings,
    setPrintSettings,
    toggleAllColumns,
    areAllColumnsSelected,
    handlePrint,
  };
}
